import ReactMapGL, {NavigationControl, Marker, Layer, Source}  from 'react-map-gl/mapbox';
import { useEffect, useMemo, useRef, useState } from 'react';
import 'mapbox-gl/dist/mapbox-gl.css';
import './styles.scss'
import MapLegend from '../images/MapLegend.png'
import classNames from 'classnames';
// import groveDot from '../images/groveDot.png'
// import groveDotSelected from '../images/groveDotSelected.png'
import useCaliforniaOutline from './useCaliforniaOutline';
import { MAPBOX_MAP_STYLE } from './mapStyles';
import { useNavigate } from 'react-router-dom';
import useParksGeojson from '../hooks/useParksGeojson';
import imagesIcon from '../images/icon-has-images.svg'
import backgroundStoryIcon from '../images/icon-has-background.svg'
import bbox from '@turf/bbox'
import centerOfMass from '@turf/center-of-mass';

const log = (f, label) => (...args) => {
  console.log(label, ...args)
  return f(...args)
}

const californiaInitialViewState = {
  longitude: -119.778,
  latitude: 37.455,
  zoom: 5.5
}
const californiaSW = [32.05482837165223, -124.98123435535132]
const californiaNE = [42.34487692872089, -113.88092125883263]

const maxBounds = [californiaSW[1] * 1.01, californiaSW[0] / 1.01, californiaNE[1] / 1.01, californiaNE[0] * 1.015]

const defaultBounds = [ -125.12565403574227, 36.23278997576564,  -118.13785376268984, 42.01376063134559,]
// visalia 36.33278997576564, -119.07917031533093
const OVERVIEW_DIFFERENCE = 5;
const OVERVIEW_MIN_ZOOM = 2.8;
const OVERVIEW_MAX_ZOOM = 19;

const overviewWidth = 115
const overviewHeight = 132.5
const buildOverviewZoom = (zoomAmount) => {
  return Math.min(Math.max(zoomAmount - OVERVIEW_DIFFERENCE, OVERVIEW_MIN_ZOOM), OVERVIEW_MAX_ZOOM);
}
const overviewInitialViewState = {
  ...californiaInitialViewState,
  zoom: buildOverviewZoom(californiaInitialViewState.zoom)
}

const initialViewState = {
  bounds: defaultBounds,
}
const californiaLayerStyle = {
  id: 'californiaOutline',
  type: 'line',
  paint: {
  }
};
const outlineLayerStyle = {
  id: 'mapOutline',
  type: 'line',
  paint: {
    'line-color': '#14816E'
  }
};

const worldWithoutCaliforniaStyle = {
  id: 'worldOutline',
  type: 'fill',
  paint: {
    'fill-color': '#ffffff',
    'fill-opacity': 0.5
  }
};


export const parksFillStyle = {
  id: 'parksFill',
  type: 'fill',
  paint: {
    'fill-color': '#FE9362',
    'fill-opacity': 0.1
  }
};

export const parksLineStyle = {
  id: 'parksLinetline',
  type: 'line',
  paint: {
    'line-color': '#FE9362'
  }
};
let mapLoadedCount = 0

export default function Map(props) {
  const { width, height, setMapLoaded, groves, interactable, selectedGroveSlug, mapLoaded, selectedPark, hoveredListItem } = props
  const [nonDefaultPitchOrRotation, setNonDefaultPitchOrRotation] = useState(false)
  const mapRef = useRef()
  const containerRef = useRef()
  const overviewMapRef = useRef()
  const [hoveredGrove, setHoveredGrove] = useState(null)
  // map starts with no opacity if it's been loaded twice, allows it to reset it's view without flashing bad state
  const [mapOpacity, setMapOpacity] = useState(mapLoadedCount > 1 ? 0 : 1)
  const parksGeojson = useParksGeojson()
  const navigate = useNavigate()
  const hoverGrove = (grove) => (e) => {
    if  (!grove) {
      setHoveredGrove(null)
      return
    }
    const mousePosition = {
      x: e.clientX - containerRef.current.offsetLeft,
      y: e.clientY - containerRef.current.offsetTop,
    }
    setHoveredGrove({
      grove,
      mousePosition,
    })
  }

  const clickGrove = (grove) => () => {
    if (interactable) {
      navigate(`/find-a-grove/${grove.slug}`)
    }
  }

  const selectedGrove = useMemo(() => selectedGroveSlug ? groves.find(g => g.slug === selectedGroveSlug) : null, [groves, selectedGroveSlug])
  const parkFeature = useMemo(() => selectedPark && parksGeojson ? parksGeojson.features.find(f => f.properties.name === selectedPark.name) : null, [parksGeojson, selectedPark])
  const parkBBox = useMemo(() => {
    if (!parkFeature) {
      return null
    }
    let box = parkFeature.properties.bbox || log(bbox, 'bbox')(parkFeature)
    //expand the bounding box by a bit in each direction
    const offset = 0.01
    const expandedBox = [
      box[0] - offset ,
      box[1] - offset,
      box[2] + offset,
      box[3] + offset,
    ]

    return expandedBox
  }, [parkFeature])

  const noGroveSelectedMapState = useRef(null)

  useEffect(() => {
    if (mapLoaded) {
      if (selectedGrove || parkBBox || selectedPark) {
        const type = selectedGrove ? 'grove' : 'park'

        if (!noGroveSelectedMapState.current || noGroveSelectedMapState.current.type !== type) {
          if (selectedGrove && !mapRef.current.getBounds().contains(selectedGrove)) {
            noGroveSelectedMapState.current = { type: 'reset' }
          } else {
            noGroveSelectedMapState.current = {
              center: mapRef.current.getCenter(),
              zoom: mapRef.current.getZoom(),
              type,
            }
          }
        }
        if (selectedGrove) {
          const { lat, lng } = selectedGrove
          if (lat && lng) {
            mapRef.current.flyTo({
              center: [lng, lat],
              zoom: 15,
            })
          }
        } else if (parkBBox) {
          mapRef.current.fitBounds(parkBBox)
        } else if (selectedPark) {
          const { lat, lng } = selectedPark
          if (lat && lng) {
            mapRef.current.flyTo({
              center: [lng, lat],
              zoom: 14,
            })
          }
        }
      } else {
        if (noGroveSelectedMapState.current) {
          if (noGroveSelectedMapState.current.type === 'reset') {
            resetBounds()
          } else {
            mapRef.current.flyTo(noGroveSelectedMapState.current)
          }
          noGroveSelectedMapState.current = null
        } else {
          resetBounds(true)
          setMapOpacity(1)
        }
      }
    }
  }, [selectedGrove, mapLoaded, parkBBox, selectedPark])

  const markers = groves.map((grove, groveIndex) => {
    if (!grove.lat || !grove.lng) return null
    const backgroundDotOpacity = grove === selectedGrove ? 0.8 : 0.4
    const backgroundDotStyle = {}
    if (grove === selectedGrove) {
      backgroundDotStyle.transform = 'scale(2)'
    }
    if (grove === hoveredGrove?.grove && !backgroundDotStyle.transform) {
      backgroundDotStyle.transform = 'scale(1.5)'
    }
    return (
      <Marker longitude={grove.lng} latitude={grove.lat} key={grove.id} >
        {/* <img
          className='groveDot'
          src={image}
          alt=''
          onClick={clickGrove(grove)}
          onMouseMove={hoverGrove(grove)}
          onMouseOver={hoverGrove(grove)}
          onMouseOut={hoverGrove(null)}
        /> */}
        <svg
          className='groveDot'
          onClick={clickGrove(grove)}
          onMouseMove={hoverGrove(grove)}
          onMouseOver={hoverGrove(grove)}
          onMouseOut={hoverGrove(null)}
          xmlns="http://www.w3.org/2000/svg" width="8.726" height="8.726" viewBox="0 0 8.726 8.726">
          <path className='backgroundDot' style={backgroundDotStyle} d="M4.363,0A4.363,4.363,0,1,1,0,4.363,4.363,4.363,0,0,1,4.363,0Z" fill="#fe9362" opacity={backgroundDotOpacity} />
          <path d="M1.87,0A1.87,1.87,0,1,1,0,1.87,1.87,1.87,0,0,1,1.87,0Z" transform="translate(2.493 2.493)" fill="#14816e" opacity="0.8"/>
        </svg>

      </Marker>
    )
  })
  const [mapBounds, setMapBounds] = useState(null)

  const [resolution, setMapResolution] = useState(1)
  const updateBounds = () => {
    if (!mapRef.current) {
      return
    }
    const lat = mapRef.current.getCenter().lat
    const zoom = mapRef.current.getZoom()
    // resolution = 156543.03 meters/pixel * cos(latitude) / (2 ^ zoomlevel)
    const resolutionMetersPerPixel = 156543.03 * Math.cos(lat * Math.PI / 180) / Math.pow(2, zoom)


    setMapResolution(resolutionMetersPerPixel)
    setMapBounds(mapRef.current.getBounds())
  }

  const resolutionMilesPerPixel = resolution / 1609.34
  const resolutionFeetPerPixel = resolution * 3.28084

  const mapLegendTargetWidth = 100
  const useMiles = resolutionMilesPerPixel > 0.01
  const mapLegendUnit = useMiles ? 'miles' : 'feet'
  const resolutionToUse = useMiles ? resolutionMilesPerPixel : resolutionFeetPerPixel

  const mapLegendTargetUnits = mapLegendTargetWidth * resolutionToUse
  const mapLegendUnits = +(mapLegendTargetUnits.toPrecision(1))

  const mapLegendWidth = mapLegendUnits / resolutionToUse * window.devicePixelRatio

  const mainMapMove = (e) => {
    // const {latitude, longitude, zoom } = e.viewState
    // overviewMapRef.current.flyTo({
    //     center: [longitude.toFixed(3), latitude.toFixed(3)],
    //     zoom: buildOverviewZoom(zoom),
    // })
    updateBounds()

  }

  const {california, worldWithoutCalifornia} = useCaliforniaOutline()
  const onMainMapLoad = () => {
    setMapLoaded(true)
    mapLoadedCount += 1
    updateBounds()
  }

  const overviewGeoJson = useMemo(() => {
    if (!mapBounds) return null
    let bounds = [];
    const neLng = Math.min(californiaNE[1] - 0.25, mapBounds._ne.lng)
    const neLat = Math.min(californiaNE[0], mapBounds._ne.lat)

    const swLng = Math.max(californiaSW[1], mapBounds._sw.lng)
    const swLat = Math.max(californiaSW[0], mapBounds._sw.lat)
    const minDelta = 0.25
    const minDeltaNorthSouth = 0.125
    const ne = [neLng, neLat]
    const se = [neLng, swLat]
    const sw = [swLng, swLat]
    const nw = [swLng, neLat]

    const center = [mapBounds.getCenter().lng, mapBounds.getCenter().lat]
    if (neLng - swLng < minDelta) {
      ne[0] = center[0] + minDelta / 2
      se[0] = center[0] + minDelta / 2
      sw[0] = center[0] - minDelta / 2
      nw[0] = center[0] - minDelta / 2
    }
    if (neLat - swLat < minDeltaNorthSouth) {
      ne[1] = center[1] + minDeltaNorthSouth / 2
      se[1] = center[1] - minDeltaNorthSouth / 2
      sw[1] = center[1] - minDeltaNorthSouth / 2
      nw[1] = center[1] + minDeltaNorthSouth / 2
    }

    bounds.push(ne, se, sw, nw, ne)
    return {
      'type': 'Feature',
      'geometry': {
          'type': 'Polygon',
          'coordinates': [bounds]
      }
    };

  }, [mapBounds])
  const mapScaleTicks = [0, mapLegendUnits / 2, mapLegendUnits]

  let groveTooltips = null
  if (hoveredGrove || selectedGrove || hoveredListItem) {
    let grove = null
    let grovePosition = { x: 0, y: 0 }
    let park = null
    const tooltipData = []
    if (selectedGrove) {
      grove = selectedGrove
      grovePosition = mapRef.current ? mapRef.current.project(grove) : grovePosition
      tooltipData.push({ grove, grovePosition, type: 'selected' })
    }
    if (hoveredGrove) {

      grove = hoveredGrove.grove
      grovePosition = hoveredGrove.mousePosition
      tooltipData.push({ grove, grovePosition, type: 'hovered' , hoverType: 1})
    } else if (hoveredListItem) {
      if ('lat' in hoveredListItem && 'park' in hoveredListItem) {
        // probably a grove
        if (mapRef.current &&  hoveredListItem.lat ) {
          grove = hoveredListItem
          grovePosition = mapRef.current.project(grove)
          tooltipData.push({ grove, grovePosition, type: 'hovered', hoverType: 2 })
        }
      } else {
        // probably a park

        const parkFeature = parksGeojson ? parksGeojson.features.find(f => f.properties.name === hoveredListItem.name) : null
        if (parkFeature && mapRef.current) {
          const parkCount = groves.filter(d => d.park === hoveredListItem.id).length
          park = {
            name: hoveredListItem.name,
            count: parkCount,
          }
          const parkCenter = parkFeature.properties.center || log(centerOfMass, 'centerOfMass')(parkFeature)
          grovePosition = mapRef.current.project(parkCenter)
          tooltipData.push({ park, grovePosition, type: 'hovered' })

        } else if ('lat' in hoveredListItem && mapRef.current) {
          const parkCount = groves.filter(d => d.park === hoveredListItem.id).length
          park = {
            name: hoveredListItem.name,
            count: parkCount,
          }
          grovePosition = mapRef.current.project(hoveredListItem)
          tooltipData.push({ park, grovePosition, type: 'hovered' })

        }

      }
    }
    // console.log(tooltipData)
    if (tooltipData.length > 1) {
      const selected = tooltipData.find(d => d.type === 'selected')
      const hovered = tooltipData.find(d => d.type === 'hovered')
      const item = selected.grove || selected.park
      const hoveredItem = hovered.grove || hovered.park
      if (item === hoveredItem) {
        tooltipData.splice(1, 1)
      }

    }
    groveTooltips = tooltipData.map((d, i) => {
      const {grove, park, grovePosition, type} = d

      if (!grovePosition || grovePosition.x < 0 || grovePosition.y < 0 || grovePosition.x > width || grovePosition.y > height) {
        return null
      }
      const tooltipPositionX = grovePosition.x + 10
      const tooltipPositionY = grovePosition.y + 10

      return grove ? (
        <div key={i} className='groveTooltip' style={{
          transform: `translate(${tooltipPositionX}px, ${tooltipPositionY}px)`,
          opacity: type === 'selected' && tooltipData.length > 1 ? 0.4 : 1,
        }}>
          <div className='groveTooltipPark'>{grove.parkData ? grove.parkData.name : ''}</div>
          <div className='groveTooltipGrove'>{grove.name}</div>
          {grove.locationEstimation ? <div className='locationEstimation'>Grove location may not be exact.</div> : null}
          {grove.images && grove.images.length ? <div className='hasImages'><img alt='has images' src={imagesIcon} /> Images included</div> : null}
          {grove.backgroundStory ? <div className='hasBackgroundStory'><img alt='has background info' src={backgroundStoryIcon} />Background information included</div> : null}
        </div>
      ) : park ? (
        <div key={i} className='groveTooltip' style={{
          transform: `translate(${tooltipPositionX}px, ${tooltipPositionY}px)`,
        }}>
          <div className='groveTooltipPark'>{park.name}</div>
          <div className='groveTooltipGrove'>{park.count} grove{park.count === 1 ? '' : 's'}</div>
        </div>
      ) : null
    })
  }

  const resetBounds = (immediate) => {
    if (mapRef.current) {
      mapRef.current.fitBounds(defaultBounds, {animate: immediate !== true})
      noGroveSelectedMapState.current = null
    }
  }
  const interactions = ['boxZoom', 'doubleClickZoom', 'dragRotate', 'dragPan', 'dragZoom', 'keyboard', 'scrollZoom', 'touchPitch', 'touchZoomRotate']
  const interactionOptions = interactions.reduce((acc, interaction) => {
    acc[interaction] = interactable
    return acc
  }, {})

  return (
    <div className={classNames("Map", { interactable } )} style={{ width, height, opacity: mapOpacity }} ref={containerRef}>
      <ReactMapGL
        ref={mapRef}
        initialViewState={initialViewState}
        mapboxAccessToken={process.env.REACT_APP_MAPBOX_ACCESS_TOKEN}
        onError={(e) => {
          console.error('Mapbox error:', e);
          console.error('Error details:', e.error);
          console.error('Mapbox token present:', !!process.env.REACT_APP_MAPBOX_ACCESS_TOKEN);
        }}
        onLoad={() => {
          console.log('Mapbox token loaded:', process.env.REACT_APP_MAPBOX_ACCESS_TOKEN ? 'Yes' : 'No');
          onMainMapLoad()
        }}
        style={{ width, height }}
        onPitch={() => setNonDefaultPitchOrRotation(true)}
        onRotation={() => setNonDefaultPitchOrRotation(true)}
        mapStyle={MAPBOX_MAP_STYLE}
        onMove={mainMapMove}
        maxBounds={maxBounds}
        {...interactionOptions}
      >
        {interactable ?
          <>
            <div data-tip="Click to reset map view" className='resetButton' onClick={resetBounds} />

            <NavigationControl position='top-left' showCompass={nonDefaultPitchOrRotation} key={nonDefaultPitchOrRotation} />
          </>
        : null }
        {markers}
        {/* <Marker longitude={defaultBounds[0]} latitude={defaultBounds[1]} />
        <Marker longitude={defaultBounds[2]} latitude={defaultBounds[3]} /> */}

        <Source id="worldOutline" type="geojson" data={worldWithoutCalifornia}>
          <Layer {...worldWithoutCaliforniaStyle} />
        </Source>

        {interactable ? (
          <Source id="parks" type="geojson" data={parksGeojson}>
            <Layer {...parksLineStyle} />
            <Layer {...parksFillStyle} />
          </Source>

        ) : null}

      </ReactMapGL>
      <div className='overviewMap'>
        <ReactMapGL
          ref={overviewMapRef}
          initialViewState={overviewInitialViewState}
          mapboxAccessToken={process.env.REACT_APP_MAPBOX_ACCESS_TOKEN}
          style={{ width: overviewWidth, height: overviewHeight }}
          interactive={false}
          mapStyle={MAPBOX_MAP_STYLE}
          attributionControl={false}
        >
          <Source id="californiaOutline" type="geojson" data={california}>
            <Layer {...californiaLayerStyle} />
          </Source>
          <Source id="boundsOutline" type="geojson" data={overviewGeoJson}>
            <Layer {...outlineLayerStyle}  />
          </Source>
        </ReactMapGL>
      </div>

      <div className='legend'>
        <img src={MapLegend} alt='map key' />
        <div className='legendShield' />
      </div>
      <div className='mapScale'>
        <svg width={mapLegendWidth + 25} height={30}>
          <rect stroke='#000' fill='#fff' width={mapLegendWidth} height={8} />
          {
            mapScaleTicks.map((tick, i) => {
              const x = tick / mapLegendUnits * mapLegendWidth
              return (
                <g key={i} transform={`translate(${x}, 0)`}>
                  <line x1={0} y1={0} x2={0} y2={16} stroke='#000' strokeWidth={1} />
                  <text x={0} y={26} fontSize={12} fill='#000' textAnchor='middle'>{tick} {i === mapScaleTicks.length - 1 ? mapLegendUnit : null}</text>
                </g>
              )
            })
          }
        </svg>
      </div>
      {groveTooltips}
    </div>
  )
}
